// src/pages/PoliciesPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TableContainer,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Checkbox, // NEW
  Tooltip,
} from '@mui/material';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material/styles';

import { Link as RouterLink } from "react-router-dom";

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear'; // NEW

import { request } from '../utils/request';

export default function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  // Delete dialog state (single)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  // three-dot menu state for per-row actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const onMenuEdit = (policy) => {
    handleMenuClose();
    handleOpen(policy); // reuses your existing edit dialog function
  };

  const onMenuDelete = (policy) => {
    handleMenuClose();
    handleDeleteClick(policy); // reuses your existing delete flow
  };

  const mode = 'light'; // or 'dark'
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    fetchPolicies();
  }, []);

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const fetchPolicies = async () => {
    try {
      const data = await request('policies', { method: 'get' });
      const arr = Array.isArray(data) ? data : [];
      // Map Mongo _id → id for convenience
      const normalized = arr.map((r) => ({
        id: r._id,
        name: r.name,
        description: r.description,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      }));
      setPolicies(normalized);
    } catch (err) {
      console.error('Error fetching Policies:', err);
      setPolicies([]);
      showSnack('Failed to fetch policies.', 'error');
    }
  };

  const handleOpen = (policy) => {
    if (policy) {
      setFormData(policy);
      setIsEditing(true);
    } else {
      setFormData({ id: null, name: '', description: '' });
      setIsEditing(false);
    }
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const opts = isEditing
        ? { method: 'put', params: formData.id, body: formData }
        : { method: 'post', body: formData };
      await request('policies', opts);
      await fetchPolicies();
      handleClose();
      showSnack(isEditing ? 'Policy updated successfully.' : 'Policy created successfully.', 'success');
    } catch (err) {
      console.error('Error saving policy:', err);
      showSnack('Failed to save policy.', 'error');
    }
  };

  // Delete handlers (single)
  const handleDeleteClick = (policy) => {
    setDeletingPolicy(policy);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingPolicy(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPolicy || !deletingPolicy.id) return;
    setDeleting(true);
    try {
      await request('policies', { method: 'delete', params: deletingPolicy.id });
      await fetchPolicies();
      showSnack(`Policy "${deletingPolicy.name}" successfully deleted.`, 'success');
      handleCancelDelete();
    } catch (err) {
      console.error('Failed to delete policy:', err);
      showSnack('Failed to delete policy.', 'error');
      setDeleting(false);
    }
  };

  // ---------------------------
  // NEW: selection & bulk-delete state + handlers
  // ---------------------------
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = policies.length > 0 && selectedIds.length === policies.length;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(policies.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const p = policies.find((r) => r.id === selectedIds[0]);
    if (p) {
      handleOpen(p);
      setSelectedIds([]); // optional: clear selection after opening edit
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => request('policies', { method: 'delete', params: id })));
      setSelectedIds([]);
      await fetchPolicies();
      handleCancelBulkDelete();
      showSnack('Selected policies deleted.', 'success');
    } catch (err) {
      console.error('Bulk delete failed:', err);
      showSnack('Failed to delete selected policies.', 'error');
      setBulkDeleting(false);
    }
  };
  // ---------------------------

  return (
    <Box p={2}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <Link
            href="/home"
            underline="hover"
            sx={{
              fontSize: '0.95rem',
              textDecoration: 'none',
              color: mode === 'dark' ? '#fff' : '#2d9cdb',
              '&:hover': {
                color: mode === 'dark' ? '#fff' : '#0b1d51',
              },
            }}
          >
            Dashboard
          </Link>

          <Typography sx={{ fontSize: '0.95rem', color:'black' }}>
            Add Policies
          </Typography>
        </Breadcrumbs>

        <Button
          size="small"
          variant="contained"
          onClick={() => handleOpen()}
          sx={{
            backgroundColor: '#2d9cdb',  px:2, py:0.8,
            '&:hover': { backgroundColor: '#0b1d51', color: '#fff' },

          }}
        >
          Add Policy
        </Button>
      </Stack>

      {/* --- Toolbar shown when rows selected (just above the table) --- */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography variant="body2">{selectedIds.length} selected</Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={handleEditSelected}
            disabled={selectedIds.length !== 1}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleDeleteSelected}
            sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' }, color: '#fff', textTransform: 'none' }}
          >
            Delete
          </Button>

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Policies Table - table-only scrolling */}
      <Paper elevation={1} sx={{ mb: 2, height: 'calc(100vh - 170px)' }}>
        <Box sx={{ height: '100%', overflow: 'auto', width: '100%' }}>
          <Box
            component="table"
            width="100%"
            sx={{ borderCollapse: 'collapse', minWidth: '100%' }}
          >
            {/* Table head with sticky headers */}
            <Box component="thead">
              <Box component="tr">
                {/* checkbox header */}
                <Box
                  component="th"
                  p={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#f5f7fb",
                    color: '#333',
                    width: 48,
                    textAlign: 'center',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'select all policies' }}
                  />
                </Box>

                {['#', 'Name', 'Created At', 'Updated At', 'Actions'].map((h, i) => (
                  <Box
                    component="th"
                    key={i}
                    p={1}
                    textAlign={i === 4 ? 'center' : 'left'}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "#f5f7fb",
                      color: '#333'
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Table body */}
            <Box component="tbody">
              {policies.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={6} p={3} textAlign="center">
                    No policies found.
                  </Box>
                </Box>
              )}
              {policies.map((p, idx) => (
                <Box component="tr" key={p.id || idx} selected={selectedIds.includes(p.id)}>
                  {/* row checkbox */}
                  <Box component="td" p={1} width="48px" textAlign="center">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      inputProps={{ 'aria-label': `select policy ${p.id}` }}
                    />
                  </Box>

                  <Box component="td" p={1} width="40px">{idx + 1}</Box>

                  <Box component="td" p={1}>
                    {p.name}
                  </Box>

                  <Box component="td" p={1}>
                    {p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}
                  </Box>

                  <Box component="td" p={1}>
                    {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}
                  </Box>

                  <Box component="td" p={1} textAlign="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, p.id)}
                        aria-controls={menuRowId === p.id ? `policy-menu-${p.id}` : undefined}
                        aria-haspopup="true"
                        sx={{ p: 0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>

                      <Menu
                        id={`policy-menu-${p.id}`}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuRowId === p.id}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem onClick={() => onMenuEdit(p)}>Edit</MenuItem>
                        <MenuItem onClick={() => onMenuDelete(p)} sx={{ color: '#d32f2f' }}>Delete</MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ backgroundColor: '#2d9cdb', color: '#fff', fontSize: '1rem', py: 1 }}
        >
          {isEditing ? 'Edit Policy' : 'Add Policy'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Box sx={{ pt: 1 }}>
              <TextField
                size="small"
                label="Policy Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <TextField
              size="small"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleClose} sx={{ color: '#2d9cdb' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: '#2d9cdb', '&:hover': { backgroundColor: '#0b1d51 !important', color: 'white' } }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (single) */}
      <Dialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-policy-dialog-title"
      >
        <DialogTitle id="delete-policy-dialog-title">Delete Policy</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the policy{' '}
            <strong>{deletingPolicy ? deletingPolicy.name : ''}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{
              backgroundColor: '#e53935',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteOpen} onClose={handleCancelBulkDelete}>
        <DialogTitle>Delete Selected Policies</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the following {selectedIds.length} policy(ies)?</Typography>
          <Box sx={{ mt: 1 }}>
            {selectedIds.map((id) => {
              const it = policies.find((r) => r.id === id);
              return <Typography key={id} variant="body2">• {it ? it.name : id}</Typography>;
            })}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBulkDelete} disabled={bulkDeleting}>Cancel</Button>
          <Button onClick={handleConfirmBulkDelete} disabled={bulkDeleting} variant="contained" color="error">
            {bulkDeleting ? 'Deleting…' : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
